import React, { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDownIcon, CheckIcon, XMarkIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';

interface SearchableMultiSelectProps {
  label: string;
  options: string[];
  selectedValues: string[];
  onOptionChange: (option: string, checked: boolean) => void;
  onChange: (values: string[]) => void;
  placeholder?: string;
  isLoading?: boolean;
  color?: string;
}


/**
 * Searchable multi-select component with string filtering
 * Ideal for large lists like failure modes where users need to search
 */
const SearchableMultiSelect: React.FC<SearchableMultiSelectProps> = ({
  label,
  options,
  selectedValues,
  onOptionChange,
  onChange,
  placeholder = 'Select options...',
  isLoading = false,
}) => {

  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownPosition, setDropdownPosition] = useState<'bottom' | 'top'>('bottom');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Filter options based on search term
  const filteredOptions = useMemo(() => {
    if (!searchTerm.trim()) return options;

    const searchLower = searchTerm.toLowerCase();
    return options.filter((option) => option.toLowerCase().includes(searchLower));
  }, [options, searchTerm]);

  // Calculate dropdown position based on available space
  useEffect(() => {
    if (isOpen && containerRef.current && dropdownRef.current) {
      const containerRect = containerRef.current.getBoundingClientRect();
      const dropdownHeight = 240; // max-h-60 = 240px
      const viewportHeight = window.innerHeight;
      const spaceBelow = viewportHeight - containerRect.bottom;
      const spaceAbove = containerRect.top;

      // Use bottom position if there's enough space, otherwise use top
      setDropdownPosition(spaceBelow >= dropdownHeight || spaceBelow > spaceAbove ? 'bottom' : 'top');
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Clear search
  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options);
    }
  };

  const handleSelectAllFiltered = () => {
    if (selectedValues.length === 0) {
      onChange(filteredOptions);
      return;
    }

    const currentSelected = new Set(selectedValues);
    const updatedSelected = selectedValues;
    for (const i in filteredOptions) {
      if (!currentSelected.has(filteredOptions[i])) {
        updatedSelected.push(filteredOptions[i]);
      }
    }
    onChange(updatedSelected);
  };

  const handleClearAllFiltered = () => {
    // remove all filtered values from selected
    const filterSet = new Set(filteredOptions);
    const filteredArray = selectedValues.filter(option => !filterSet.has(option));

    onChange(filteredArray);
  }

  const handleClearAll = () => {
    onChange([]);
  }

  return (
    <div className="relative">
      <label className="block text-sm onyx-subtitle text-gray-700 mb-2">
        {label}
        <span className="text-gray-400 onyx-body ml-1">({options.length})</span>
      </label>

      <div className="relative" ref={containerRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          disabled={isLoading}
          className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white text-left shadow-sm hover:border-gray-400 focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue disabled:opacity-50 transition-colors onyx-body"
        >
          <span className={`${selectedValues.length > 0 ? 'text-gray-900' : 'text-gray-500'}`}>
            {selectedValues.length === 0 ? placeholder : `${selectedValues.length} selected`}
          </span>
          <ChevronDownIcon
            className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          />
        </button>

        {isOpen && (
          <div
            ref={dropdownRef}
            className={`absolute z-50 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto ${dropdownPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'
              }`}
          >
            {options.length > 1 && !searchTerm && (
              <div className='items-center grid grid-cols-3 w-full border-b border-gray-200'>
                <div
                  onClick={handleSelectAll}
                  className="col-span-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <div className="flex items-center">
                    <div className={`
                    w-4 h-4 border-2 rounded mr-3 flex items-center justify-center
                    ${selectedValues.length === options.length
                        ? 'bg-blue-600 border-blue-600'
                        : selectedValues.length > 0
                          ? 'bg-blue-100 border-blue-600'
                          : 'border-gray-300'
                      }
                  `}>
                      {selectedValues.length === options.length && (
                        <CheckIcon className="w-3 h-3 text-white" />
                      )}
                      {selectedValues.length > 0 && selectedValues.length < options.length && (
                        <div className="w-2 h-2 bg-blue-600 rounded-sm" />
                      )}
                    </div>
                    <span className="text-sm font-medium text-gray-700">
                      {selectedValues.length === options.length ? 'Deselect All' : 'Select All'}
                    </span>
                  </div>
                </div>

                {selectedValues.length > 0 && selectedValues.length < options.length && (
                  <div onClick={handleClearAll} className="col-span-auto px-3 py-2 hover:bg-gray-100 cursor-pointer">
                    <span className="text-sm text-right font-medium text-red-700">
                      Clear
                    </span>
                  </div>
                )}

              </div>
            )}
            {options.length > 1 && searchTerm && (
              <div className='items-center grid grid-cols-3 w-full border-b border-gray-200'>
                <div
                  onClick={handleSelectAllFiltered}
                  className="col-span-2 px-3 py-2 hover:bg-gray-100 cursor-pointer"
                >
                  <div className="flex items-center">
                    <span className="text-sm font-medium text-gray-700">
                      Select Subset
                    </span>
                  </div>
                </div>

                {selectedValues.length > 0 && (
                  <div onClick={handleClearAllFiltered} className="col-span-auto px-3 py-2 hover:bg-gray-100 cursor-pointer">
                    <span className="text-sm text-right font-medium text-red-700">
                      Clear
                    </span>
                  </div>
                )}

              </div>
            )}
            <div className="p-2">
              {/* Search input */}
              <div className="relative mb-2">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search failure modes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-8 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-onyx-bright-blue focus:border-onyx-bright-blue onyx-body"
                />
                {searchTerm && (
                  <button
                    onClick={handleClearSearch}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                )}
              </div>

              {filteredOptions.length === 0 ? (
                <div className="p-2 text-center text-gray-500 onyx-body text-sm">
                  {searchTerm ? 'No matching failure modes found' : 'No options available'}
                </div>
              ) : (
                filteredOptions.map((option) => (
                  <label
                    key={option}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-50 rounded cursor-pointer transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={selectedValues.includes(option)}
                      onChange={(e) => onOptionChange(option, e.target.checked)}
                      className="rounded border-gray-300 text-onyx-bright-blue focus:ring-onyx-bright-blue"
                    />
                    <span className="text-sm text-gray-700 flex-1 onyx-body">{option}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchableMultiSelect;
